'use client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import { yupResolver } from '@hookform/resolvers/yup';

import Input from '../Input';
import CountrySelect from '../CountrySelect';
import MultiRangeSlider from '../MultiRangeSlider';
import Button from '../Button';
import TextArea from '../Textarea';
import DatePicker from '../DatePicker';
import FormTypeToggle from './FormTypeToggle';
import SuccessModal from '../SuccessModal';

import { getTranslatedSchema } from './schema';
import ImageSelector from '../ImageSelector';

import { cn } from '@/lib/utils';
import {
  IContactPageDictionary,
  IContactPageFormDictionary,
} from '@/dictionaries/pages/contacts/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { useCurrency } from '@/contexts/currencyContext';
import { postContact } from '@/actions/contacts';
import useCountries from '@/hooks/useCountries';
import { postMedia } from '@/actions/applyNow';
import { GBP_MULTIPLIER, USD_MULTIPLIER } from '@/utils/currency';
import { formatLocalizedNumber } from '@/utils/village';

type Props = {
  contactDict: IContactPageDictionary;
  formDict: IContactPageFormDictionary;
  datePickerDict: IDatePickerDictonary;
  countriesDict: ICountriesDictionary;
  lang: string;
};

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function ContactForm({
  contactDict,
  formDict,
  datePickerDict,
  countriesDict,
  lang,
}: Props) {
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [sliderMin, setSliderMin] = useState(1000);
  const [sliderMax, setSliderMax] = useState(50000);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [blockSubmit, setBlockSubmit] = useState(false);

  const { currency } = useCurrency();

  const schema = getTranslatedSchema(formDict.errors);
  const { translatedCountries, languageCountry } = useCountries({
    lang,
    countriesDict,
  });

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      form_type: 'guest',
      country_info: {
        name: languageCountry?.name,
        code: languageCountry?.code,
        id: languageCountry?.id,
        phone: {
          code: languageCountry?.phoneCode,
          country: languageCountry?.name,
        },
      },
      terms: false,
    },
  });

  const formType = watch('form_type');

  useEffect(() => {
    if (successModalIsOpen) {
      reset();
      setValue('terms', false);
    }
  }, [successModalIsOpen, reset, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    if (blockSubmit) return;
    setBlockSubmit(true);

    function applyCurrency(value: number) {
      let newValue: number;

      if (currency.value === 'USD') {
        newValue = value / USD_MULTIPLIER;
        return Number(newValue.toFixed(2));
      }

      if (currency.value === 'GBP') {
        newValue = value / GBP_MULTIPLIER;
        return Number(newValue.toFixed(2));
      }

      return value;
    }

    window.dataLayer = window.dataLayer || [];

    const minBudget = data.budget.min ? applyCurrency(data.budget.min) : null;
    const maxBudget = data.budget.max ? applyCurrency(data.budget.max) : null;

    const commonValues = {
      firstName: data.name,
      lastName: data.surname,
      email: data.email,
      phoneCode: data.country_info.id,
      phone: data.phone.toString(),
      address: {
        street: data.villa_address ?? null,
        postalCode: data.post_code ?? null,
        city: data.town_country ?? null,
        country: data.country_info.id,
      },
      type: 'customer_request',
      privacyPolicy: data.terms,
      // UTC day of the picked date; null when none was picked
      showStartDate: data.dates.start?.toISOString().slice(0, 10) ?? null,
      showEndDate: data.dates.end?.toISOString().slice(0, 10) ?? null,
      language: lang,
    };

    if (data.form_type == 'guest') {
      const requestData = {
        ...commonValues,
        options: {
          choice: 'guest',
          startDate: data.dates.start?.toISOString() ?? null,
          endDate: data.dates.end?.toISOString() ?? null,
          description: data.message,
          budget: [data.budget.min, data.budget.max],
          eurBudget: [minBudget, maxBudget],
        },
      };

      try {
        const response = await postContact(requestData);
        if (response.enquiry_id || response?.data) {
          setSuccessModalIsOpen(true);
          setBlockSubmit(false);

          window.dataLayer.push({
            event: 'eventContactTourist',
            eventCategory: 'send_contact',
            eventAction: 'form_tourist',
            eventLabel: window.location.href,
            language: lang,
          });
        } else {
          setBlockSubmit(false);
        }
      } catch (error) {
        console.log('CONTACT-GUEST', error);
      }
    }

    if (data.form_type == 'owner') {
      const requestData = {
        ...commonValues,
        files: [{}],
        options: {
          choice: 'owner',
          startDate: data.dates.start,
          endDate: data.dates.end,
          description: data.property_description,
          budget: [data.budget.min, data.budget.max],
          eurBudget: [minBudget, maxBudget],
          website: data.website,
        },
        type: 'owner_request',
      };

      try {
        const response = await postContact(requestData);
        if (response.enquiry_id || response?.data) {
          const formData = new FormData();

          formData.append('lead', response.enquiry_id);

          uploadedImages.map((file, index) => {
            formData.append(`files[${index}]`, file);
          });

          const res = await postMedia(formData);
          setSuccessModalIsOpen(true);
          setBlockSubmit(false);

          window.dataLayer.push({
            event: 'eventContactOwner',
            eventCategory: 'send_contact',
            eventAction: 'form_owner',
            eventLabel: window.location.href,
            language: lang,
          });
        } else {
          setBlockSubmit(false);
        }
      } catch (error) {
        console.log('CONTACT-OWNER', error);
      }
    }
  });

  return (
    <form onSubmit={onSubmit}>
      {successModalIsOpen && (
        <SuccessModal
          modalIsOpen={successModalIsOpen}
          setModalIsOpen={setSuccessModalIsOpen}
          title={contactDict.successModal.title}
          description={contactDict.successModal.description}
        />
      )}
      <div className="flex flex-col">
        <FormTypeToggle
          dict={formDict}
          formType={formType}
          onToggle={(value) => setValue('form_type', value)}
        />
        <div className="flex flex-col gap-4 tablet:gap-0 tablet:flex-row tablet:justify-between">
          <Input.Container className="tablet:w-[calc(50%-1rem)]">
            <Input.Label htmlFor="name">{formDict.name}</Input.Label>
            <Input.Root register={register} name="name" />
            <Input.Error error={errors.name} />
          </Input.Container>

          <Input.Container className="tablet:w-[calc(50%-1rem)]">
            <Input.Label htmlFor="surname">{formDict.surname}</Input.Label>
            <Input.Root register={register} name="surname" />
            <Input.Error error={errors.surname} />
          </Input.Container>
        </div>
        {formType == 'owner' && (
          <div className="flex flex-col gap-4 mt-4 tablet:gap-0 tablet:flex-row tablet:justify-between">
            <Input.Container className="tablet:w-[calc(50%-1rem)]">
              <Input.Label htmlFor="villa_address">
                {formDict.villaAddress}
              </Input.Label>
              <Input.Root register={register} name="villa_address" />
              <Input.Error error={errors.villa_address} />
            </Input.Container>

            <Input.Container className="tablet:w-[calc(50%-1rem)]">
              <Input.Label htmlFor="town_country">
                {formDict.townCountry}
              </Input.Label>
              <Input.Root register={register} name="town_country" />
            </Input.Container>
          </div>
        )}
        {formType == 'owner' ? (
          <>
            <div className="flex flex-col gap-4 mt-4 tablet:gap-0 tablet:flex-row tablet:justify-between">
              <Input.Container className="tablet:w-[calc(50%-1rem)]">
                <Input.Label htmlFor="post_code">
                  {formDict.postCode}
                </Input.Label>
                <Input.Root
                  register={register}
                  name="post_code"
                  maxLength={5}
                />
                <Input.Error error={errors.post_code} />
              </Input.Container>

              <div className="tablet:w-[calc(50%-1rem)] h-fit">
                <CountrySelect
                  countries={translatedCountries}
                  label={formDict.country}
                  id={'country-selector'}
                  onChange={({ code, name, id }) => {
                    setValue('country_info.name', name);
                    setValue('country_info.code', code);
                    setValue('country_info.id', id);
                  }}
                  selectedValue={translatedCountries.find(
                    (option) => option.name === watch('country_info.name'),
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4 mt-4 tablet:gap-0 tablet:flex-row tablet:justify-between">
              <Input.Container className="tablet:w-[calc(50%-1rem)]">
                <Input.Label htmlFor="email">{formDict.email}</Input.Label>
                <Input.Root register={register} name="email" />
                <Input.Error error={errors.email} />
              </Input.Container>

              <div className="flex flex-col tablet:w-[calc(50%-1rem)]">
                <label
                  className="text-sm text-gray-700 leading-[1.8] w-fit"
                  htmlFor="phone-country-selector">
                  {formDict.phone}
                </label>
                <div className="flex flex-row">
                  <div className="w-[250px] tablet:w-[280px] desktop:w-[200px] h-fit">
                    <CountrySelect
                      countries={translatedCountries}
                      label=""
                      id={'phone-country-selector'}
                      showPhoneCode
                      onChange={({ name, id }) => {
                        setValue('country_info.phone.code', id);
                        setValue('country_info.phone.country', name);
                      }}
                      selectedValue={translatedCountries.find(
                        (option) =>
                          option.name === watch('country_info.phone.country'),
                      )}
                    />
                  </div>
                  <Input.Container className="w-full">
                    <Input.Root register={register} name="phone" />
                    <Input.Error error={errors.phone} />
                  </Input.Container>
                </div>
              </div>
            </div>

            <Input.Container className="mt-4">
              <Input.Label htmlFor="website">
                {formDict.website} <i>{formDict.optional}</i>
              </Input.Label>
              <Input.Root register={register} name="website" />
            </Input.Container>

            <TextArea
              className="mt-4"
              register={register}
              name="property_description"
              error={errors.property_description}
              label={formDict.propertyDesc}
            />
          </>
        ) : (
          <>
            <div className="flex flex-col gap-4 mt-4 tablet:gap-0 tablet:flex-row tablet:justify-between">
              <div className="tablet:w-[calc(25%-1rem)] h-fit">
                <CountrySelect
                  countries={translatedCountries}
                  label={formDict.country}
                  id={'country-selector'}
                  onChange={({ code, name, id }) => {
                    setValue('country_info.name', name);
                    setValue('country_info.code', code);
                    setValue('country_info.id', id);
                  }}
                  selectedValue={translatedCountries.find(
                    (option) => option.name === watch('country_info.name'),
                  )}
                />
              </div>
              <Input.Container className="tablet:w-[calc(41%-1rem)]">
                <Input.Label htmlFor="email">{formDict.email}</Input.Label>
                <Input.Root register={register} name="email" />
                <Input.Error error={errors.email} />
              </Input.Container>

              <div className="flex flex-col tablet:w-[calc(33%-1rem)]">
                <label
                  className="text-sm text-gray-700 leading-[1.8] w-fit"
                  htmlFor="phone-country-selector">
                  {formDict.phone}
                </label>
                <div className="flex flex-row">
                  <div className="w-[250px] tablet:w-[280px] desktop:w-[200px] h-fit">
                    <CountrySelect
                      countries={translatedCountries}
                      label=""
                      id={'phone-country-selector'}
                      showPhoneCode
                      onChange={({ name, id }) => {
                        setValue('country_info.phone.code', id);
                        setValue('country_info.phone.country', name);
                      }}
                      selectedValue={translatedCountries.find(
                        (option) =>
                          option.name === watch('country_info.phone.country'),
                      )}
                    />
                  </div>
                  <Input.Container className="w-full">
                    <Input.Root register={register} name="phone" />
                    <Input.Error error={errors.phone} />
                  </Input.Container>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 my-4 tablet:gap-0 desktop:flex-row desktop:justify-between">
              <div className="flex flex-col w-[calc(100vw-80px)] desktop:w-[calc(50%-1rem)]">
                <div className="flex pt-4 justify-between">
                  <label className="text-sm text-gray-700 leading-[1.8] w-fit">
                    {formDict.travelDates} <i>{`(${formDict.optional})`}</i>
                  </label>
                </div>
                <DatePicker.Root
                  open={modalIsOpen}
                  onOpenChange={setModalIsOpen}>
                  <DatePicker.Trigger
                    dict={datePickerDict}
                    confirmedDates={watch('dates')}
                  />
                  <DatePicker.Content
                    confirmedDates={watch('dates')}
                    confirmDates={(value) => {
                      successModalIsOpen
                        ? setValue('dates', { start: null, end: null })
                        : setValue('dates', value);
                    }}
                    onOpenChange={setModalIsOpen}
                    dict={datePickerDict}
                  />
                </DatePicker.Root>
              </div>
              <div className="flex flex-col w-[calc(100vw-80px)] desktop:w-1/2">
                <div className="flex py-4 justify-between">
                  <label className="text-sm text-gray-700 leading-[1.8] w-fit">
                    {formDict.budget} <i>{`(${formDict.optional})`}</i>
                  </label>
                  <p className="text-sm">{`${
                    currency.symbol
                  } ${formatLocalizedNumber(sliderMin, lang)} — ${
                    currency.symbol
                  } ${formatLocalizedNumber(sliderMax, lang)}`}</p>
                </div>
                <MultiRangeSlider
                  min={1000}
                  max={50000}
                  onChange={({ min, max }) => {
                    setSliderMin(min);
                    setValue('budget.min', sliderMin);
                    setSliderMax(max);
                    setValue('budget.max', sliderMax);
                  }}
                />
              </div>
            </div>

            <TextArea
              className="mt-4"
              register={register}
              name="message"
              label={formDict.message}
              error={errors.message}
            />
          </>
        )}

        <div className="flex flex-col gap-4 mt-4">
          <div className="w-full flex flex-col gap-4">
            {formType == 'owner' && (
              <div className="flex flex-col w-full">
                <label className="text-sm text-footer-gray">
                  {formDict.upload}
                  <i className="ml-2 font-averta-italic">(Max. 2MB)</i>
                </label>
                <ImageSelector
                  files={uploadedImages}
                  removeFile={(targetFile) => {
                    setUploadedImages(
                      uploadedImages.filter((file) => file != targetFile),
                    );
                  }}
                  onChange={(file) => {
                    setUploadedImages((prev) => [...prev, file]);
                  }}>
                  {formDict.addPhotos}
                </ImageSelector>
              </div>
            )}
            <div className="flex flex-col">
              <div className="flex items-start gap-4 tablet:w-80 lg:w-[30rem] desktop:w-full">
                <input
                  className={cn('checkbox-input w-6 h-6', {
                    'border border-underline': watch('terms') === true,
                  })}
                  {...register('terms')}
                  onChange={(e) => setValue('terms', e.target.checked)}
                  type="checkbox"
                  name="checkbox-input"
                />
                <label
                  className="text-sm text-footer-gray"
                  htmlFor="checkbox-input">
                  {formDict.terms}
                  <Link
                    href={formDict.privacyHref}
                    target="_blank"
                    className="hover:text-gray-700 underline cursor-pointer ml-1 text-footer-gray">
                    {formDict.privacyPolicy}
                  </Link>
                </label>
              </div>
              <Input.Error error={errors.terms} />
            </div>
          </div>

          <div className="w-full flex items-end">
            <Button
              type="submit"
              className={cn(
                'tablet:ml-auto tablet:w-[calc(50%-1rem)] desktop:w-[calc(33%-1rem)]',
                {
                  'opacity-70': isSubmitting,
                  'hover:bg-primary hover:text-white': blockSubmit,
                },
              )}
              disabled={blockSubmit}>
              {formDict.sendRequest}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
